import {isEdgeBrowser, isFirefoxBrowser, isSafariBrowser} from '../utils/browsers';
import {getCxenseProperties} from '../utils/cxense';
import {getGigyaId} from '../utils/gigya';
import pubsub from '../utils/pubsub';
import querystring from '../utils/querystring';
import {getSnowplowId} from '../utils/snowplow';
import breakpoints from './breakpoints';
import messaging from './messaging';
import {hasUserProfilingConsent, hasVendorsConsent, hasXandrConsent} from './privacyService';
import Settings from '../domain/Settings';
import {getActiveExperiments} from './experimentService';
import storage from '../utils/storage';
import {getPageViewId} from "../utils/page-view-id";

function _getMetaTargeting(userProfilingConsent: boolean) {
	const metaTargeting: Targeting = {
		'screen': breakpoints.getCurrentBreakpoint(),
		'authKey': messaging.getAuthKey(),
		'targeted': userProfilingConsent,
		'adops': querystring.get('adops'),
		'thirdpartycookie': isFirefoxBrowser() || isSafariBrowser() || isEdgeBrowser() ? 0 : 1
	};

	_addLegacyExperiments(metaTargeting);
	_addExperiments(metaTargeting);
	_addPageViewId(metaTargeting);
	_addSimpleAds(metaTargeting);

	return metaTargeting;
}

function _getUserTargeting(userProfilingConsent: boolean) {
	const userTargeting = {};

	if (userProfilingConsent) {
		_addSnowplow(userTargeting);
		_addGigya(userTargeting);

		if (hasVendorsConsent([412])) {
			_addCxense(userTargeting);
		}
	}

	return userTargeting;
}

function _getTargeting(targeting: Targeting) {
	const userProfilingConsent: boolean = hasUserProfilingConsent(),

		metaTargeting = _getSafeTargeting(_getMetaTargeting(userProfilingConsent));

	// Meta applied twice so it's not overwritten, but still first
	return Object.assign({}, metaTargeting, _getSafeTargeting(targeting), metaTargeting, _getSafeTargeting(_getUserTargeting(userProfilingConsent)));
}

export function setPageTargeting(targeting: Targeting) {
	Settings.getInstance().targeting = targeting;

	pubsub.publish('settings.changed');
}

export function getPageTargeting() {
	return _replacePlaceholders(_getTargeting(Settings.getInstance().targeting as Targeting));
}

export function getSlotTargeting(/** Slot */ slot: SlotConfig) {
	const slotTargeting = {
		'slot': slot.name,
		...slot.targeting
	};

	return _replacePlaceholders(_getSafeTargeting(slotTargeting), slot);
}

function _getSafeTargeting(targeting: Targeting) {
	return Object.keys(targeting).reduce((t: Targeting, key) => {
		const value = _getSafeTargetValue(targeting[key]);

		if (typeof value !== 'undefined' && value !== null) {
			t[key] = value;
		}

		return t;
	}, {});
}

function _getSafeTargetValue(value: TargetValue | Array<TargetValue | Array<unknown>>): TargetValue | Array<TargetValue> {
	if (typeof value === 'string') {
		return value;
	}

	if (typeof value === 'boolean' || typeof value === 'number') {
		return value.toString();
	}

	if (Array.isArray(value)) {
		return value.map((val) => {
			if (Array.isArray(val)) {
				return null;
			}

			return _getSafeTargetValue(val);
		}).filter((val) => val !== null) as Array<TargetValue>;
	}

	return null;
}

function _replacePlaceholders(targeting: Targeting, slot?: SlotConfig) {
	Object.keys(targeting).forEach((field) => {
		if (typeof targeting[field] === 'string') {
			targeting[field] = (targeting[field] as string).replace('%screenSize%', breakpoints.getCurrentBreakpoint());

			if (slot) {
				targeting[field] = (targeting[field] as string).replace('%slotName%', slot.name);
			}
		}
	});

	return targeting;
}

function _addCxense(targeting: Targeting) {
	if (!Settings.getInstance().features.cxense) {
		return;
	}

	const cxenseProperties = getCxenseProperties();

	if (cxenseProperties) {
		targeting.cxid = cxenseProperties.id;
		targeting.cxsg = cxenseProperties.segments;
		// eslint-disable-next-line camelcase
		targeting.cxsg_m = cxenseProperties.marketingSegments;
	}
}

function _addSnowplow(targeting: Targeting) {
	if (!Settings.getInstance().features.snowplow) {
		return;
	}

	targeting.spid = getSnowplowId();
}

function _addGigya(targeting: Targeting) {
	const gigyaFeature = Settings.getInstance().features.gigya;

	if (!gigyaFeature) {
		return;
	}

	if (typeof gigyaFeature === 'string') {
		targeting.gigyaid = getGigyaId(gigyaFeature);
	} else {
		targeting.gigyaid = getGigyaId();
	}
}

function _addExperiments(targeting: Targeting) {
	const experiments = getActiveExperiments();

	if (experiments.length > 0) {
		targeting.experiments = experiments.map((experiment) => `${experiment.name}_${experiment.group}`);
	}
}

function _addLegacyExperiments(targeting: Targeting) {
	if (!Settings.getInstance().features.experiments) {
		return;
	}

	targeting.exp = storage.getFromCookie('exp');
}

function _addPageViewId(targeting: Targeting) {
	const pageViewId = getPageViewId();

	if (pageViewId) {
		targeting.pageviewid = pageViewId;
	}
}

function _addSimpleAds(targeting: Targeting) {
	if (!Settings.getInstance().features.xandrSimpleAds) {
		return;
	}

	targeting.simpleads = !hasXandrConsent();
}
